import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { masterFirebaseService } from '../services/firebaseService';
import { ClienteResumo } from '../types';
import {
  Search,
  Building2,
  GraduationCap,
  ChevronRight,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Loader2,
  RefreshCw,
  Filter,
} from 'lucide-react';

const Clientes: React.FC = () => {
  const [clientes, setClientes] = useState<ClienteResumo[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filtroStatus, setFiltroStatus] = useState<'todos' | 'ativos' | 'inativos'>('todos');
  const [filtroFinanceiro, setFiltroFinanceiro] = useState<'todos' | 'em_dia' | 'atraso' | 'sem_assinatura'>('todos');
  const [alterandoStatus, setAlterandoStatus] = useState<string | null>(null);
  const navigate = useNavigate();

  const loadClientes = async () => {
    setLoading(true);
    try {
      const data = await masterFirebaseService.getClientesResumo();
      setClientes(data);
    } catch (error) {
      console.error('Erro ao carregar clientes:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadClientes();
  }, []);

  const handleAlterarStatus = async (cnpj: string, novoStatus: boolean) => {
    setAlterandoStatus(cnpj);
    try {
      await masterFirebaseService.alterarStatusEmpresa(cnpj, novoStatus);
      setClientes(prev =>
        prev.map(c => c.cnpj === cnpj ? { ...c, status: novoStatus } : c)
      );
    } catch (error) {
      console.error('Erro ao alterar status:', error);
    } finally {
      setAlterandoStatus(null);
    }
  };

  const clientesFiltrados = useMemo(() => {
    return clientes.filter(c => {
      const matchSearch = search === '' ||
        c.nomeFantasia.toLowerCase().includes(search.toLowerCase()) ||
        c.cnpj.includes(search.replace(/\D/g, ''));

      const matchStatus =
        filtroStatus === 'todos' ||
        (filtroStatus === 'ativos' && c.status) ||
        (filtroStatus === 'inativos' && !c.status);

      const matchFinanceiro =
        filtroFinanceiro === 'todos' ||
        c.statusFinanceiro === filtroFinanceiro;

      return matchSearch && matchStatus && matchFinanceiro;
    });
  }, [clientes, search, filtroStatus, filtroFinanceiro]);

  const formatCNPJ = (cnpj: string) => {
    const n = cnpj.replace(/\D/g, '');
    return n.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
  };

  const StatusFinanceiroBadge: React.FC<{ status: string }> = ({ status }) => {
    switch (status) {
      case 'em_dia':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
            <CheckCircle className="h-3 w-3" /> Em dia
          </span>
        );
      case 'atraso':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
            <AlertTriangle className="h-3 w-3" /> Em atraso
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
            <XCircle className="h-3 w-3" /> Sem assinatura
          </span>
        );
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Clientes</h1>
          <p className="text-sm text-gray-500 mt-1">{clientes.length} empresas cadastradas</p>
        </div>
        <button
          onClick={loadClientes}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <RefreshCw className="h-4 w-4" />
          Atualizar
        </button>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="p-4 border-b border-gray-200 space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por nome ou CNPJ..."
              className="input pl-10"
            />
          </div>

          <div className="flex flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-gray-400" />
              <select
                value={filtroStatus}
                onChange={(e) => setFiltroStatus(e.target.value as any)}
                className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 bg-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="todos">Todos os status</option>
                <option value="ativos">Ativos</option>
                <option value="inativos">Inativos</option>
              </select>
            </div>

            <select
              value={filtroFinanceiro}
              onChange={(e) => setFiltroFinanceiro(e.target.value as any)}
              className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 bg-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="todos">Financeiro: Todos</option>
              <option value="em_dia">Em dia</option>
              <option value="atraso">Em atraso</option>
              <option value="sem_assinatura">Sem assinatura</option>
            </select>
          </div>
        </div>

        {clientesFiltrados.length === 0 ? (
          <div className="text-center py-12">
            <Building2 className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">Nenhum cliente encontrado.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Empresa</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden md:table-cell">CNPJ</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden lg:table-cell">Plano</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden sm:table-cell">Alunos</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Financeiro</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {clientesFiltrados.map((cliente) => (
                  <tr
                    key={cliente.cnpj}
                    className="hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => navigate(`/clientes/${cliente.cnpj}`)}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                          <Building2 className="h-4 w-4 text-primary-600" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{cliente.nomeFantasia}</p>
                          <p className="text-xs text-gray-500 truncate">{cliente.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      <span className="text-sm text-gray-600 font-mono">{formatCNPJ(cliente.cnpj)}</span>
                    </td>
                    <td className="px-4 py-3 hidden lg:table-cell">
                      <div>
                        <p className="text-sm text-gray-900">{cliente.planoNome}</p>
                        {cliente.planoValor > 0 && (
                          <p className="text-xs text-gray-500">
                            R$ {cliente.planoValor.toFixed(2).replace('.', ',')}
                          </p>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center hidden sm:table-cell">
                      <div className="flex items-center justify-center gap-1">
                        <GraduationCap className="h-3.5 w-3.5 text-gray-400" />
                        <span className="text-sm text-gray-700">{cliente.totalAlunos}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <StatusFinanceiroBadge status={cliente.statusFinanceiro} />
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAlterarStatus(cliente.cnpj, !cliente.status);
                        }}
                        disabled={alterandoStatus === cliente.cnpj}
                        className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                          cliente.status
                            ? 'bg-green-100 text-green-700 hover:bg-green-200'
                            : 'bg-red-100 text-red-700 hover:bg-red-200'
                        }`}
                      >
                        {alterandoStatus === cliente.cnpj ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          cliente.status ? 'Ativo' : 'Inativo'
                        )}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <ChevronRight className="h-4 w-4 text-gray-400 inline-block" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Clientes;

